import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';


import { API_URL } from '../../utils/apiUrl';

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ========== HOOK ==========

const usePushNotifications = () => {
  const { user, token } = useAuth();
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
   
  }, []);

  useEffect(() => {
    if (!user || !token) return;
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/notifications/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscribed(res.data.subscribed);
        setPreferences(res.data.preferences || {});
      } catch {
        // Not subscribed
      }
    };
    checkStatus();
   
  }, [user, token]);

  const subscribeToPush = useCallback(async () => {
    if (!user || !token || !('serviceWorker' in navigator)) return;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { setLoading(false); return; }

      const vapidRes = await axios.get(`${API_URL}/api/notifications/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(vapidRes.data.public_key);
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      await axios.post(
        `${API_URL}/api/notifications/subscribe`,
        { subscription: subscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubscribed(true);
      setPreferences({
        anniversari: true, streaming: true,
        ordini_fiori: true, abbonamento: true, contatti: true
      });
    } catch (err) {
      console.error('Push subscription failed:', err);
    } finally {
      setLoading(false);
    }
   
  }, [user, token]);

  const updatePreference = async (key, value) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    try {
      await axios.put(
        `${API_URL}/api/notifications/preferences`,
        updated,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      setPreferences(preferences);
    }
  };

  const getNotificationTypes = () => {
    if (!user) return [];
    if (user.tipo === 'agenzia') {
      return [
        { key: 'ordini_fiori', label: 'Nuovi ordini fiori', icon: 'local_florist' },
        { key: 'abbonamento', label: 'Scadenza abbonamento', icon: 'credit_card' },
        { key: 'contatti', label: 'Richieste di contatto', icon: 'mail' },
        { key: 'streaming', label: 'Dirette streaming', icon: 'videocam' },
      ];
    }
    return [
      { key: 'anniversari', label: 'Anniversari e ricorrenze', icon: 'event' },
      { key: 'streaming', label: 'Dirette streaming funerali', icon: 'videocam' },
    ];
  };

  return {
    permission, subscribed, preferences, loading,
    subscribeToPush, updatePreference, getNotificationTypes
  };
};

// ========== BANNER COMPONENT ==========

export const PushNotificationBanner = () => {
  const { user } = useAuth();
  const { subscribed, subscribeToPush, loading, permission } = usePushNotifications();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!user || subscribed || permission === 'denied') return;
    const dismissed = localStorage.getItem('push-banner-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;
    const timer = setTimeout(() => setShowBanner(true), 8000);
    return () => clearTimeout(timer);
   
  }, [user, subscribed, permission]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('push-banner-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed top-20 left-3 right-3 z-[1000] md:left-auto md:right-4 md:max-w-sm animate-slide-down" data-testid="push-notification-banner">
      <div className="bg-white border border-amber-200 rounded-2xl shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-amber-600">notifications_active</span>
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-sm text-gray-900">Attiva le notifiche</h4>
            <p className="text-xs text-gray-600 mt-0.5">
              Ricevi avvisi per anniversari, dirette streaming e aggiornamenti importanti.
            </p>
          </div>
          <button onClick={dismissBanner} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={async () => { await subscribeToPush(); setShowBanner(false); }}
            disabled={loading}
            className="flex-1 py-2 bg-amber-600 text-white text-sm font-bold rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="push-enable-btn"
          >
            {loading ? (
              <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
            ) : (
              <span className="material-symbols-outlined text-sm">notifications</span>
            )}
            Attiva
          </button>
          <button onClick={dismissBanner} className="px-4 py-2 text-gray-600 text-sm rounded-xl hover:bg-gray-100">
            Non ora
          </button>
        </div>
      </div>
    </div>
  );
};

// ========== PREFERENCES PANEL COMPONENT ==========

export const NotificationPreferencesPanel = ({ className = '' }) => {
  const { subscribed, preferences, subscribeToPush, updatePreference, loading, permission, getNotificationTypes } = usePushNotifications();
  const types = getNotificationTypes();

  if (types.length === 0) return null;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-5 ${className}`} data-testid="notification-preferences">
      <div className="flex items-center gap-3 mb-4">
        <span className="material-symbols-outlined text-amber-600">notifications</span>
        <h3 className="font-bold text-espresso">Notifiche Push</h3>
      </div>

      {!subscribed ? (
        <div className="text-center py-4">
          {permission === 'denied' ? (
            <p className="text-sm text-gray-500">
              Le notifiche sono state bloccate. Abilitale dalle impostazioni del browser.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-3">
                Attiva le notifiche per ricevere avvisi importanti direttamente sul tuo dispositivo.
              </p>
              <button
                onClick={subscribeToPush}
                disabled={loading}
                className="px-6 py-2.5 bg-amber-600 text-white font-semibold rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto"
                data-testid="push-enable-settings-btn"
              >
                <span className="material-symbols-outlined text-sm">notifications_active</span>
                Attiva Notifiche
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-green-600 font-medium flex items-center gap-1 mb-2">
            <span className="material-symbols-outlined text-sm">check_circle</span>
            Notifiche attive
          </p>
          {types.map((type) => (
            <label key={type.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-gray-500 text-xl">{type.icon}</span>
                <span className="text-sm font-medium text-gray-800">{type.label}</span>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={preferences[type.key] !== false}
                  onChange={(e) => updatePreference(type.key, e.target.checked)}
                  className="sr-only peer"
                  data-testid={`pref-toggle-${type.key}`}
                />
                <div className="w-10 h-5 bg-gray-300 peer-checked:bg-amber-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default usePushNotifications;
